// src/components/backend/pages/BlockedDomainsPage.jsx
import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, Button, Box, CircularProgress, Alert, Typography, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, IconButton, Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LabelIcon from "@mui/icons-material/Label";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonIcon from "@mui/icons-material/Person";
import PageContainer from "../layout/PageContainer";
import useBlockedDomains from "../../hooks/useBlockedDomains";

const fmtDE = new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
});

export default function BlockedDomains() {
    const [query, setQuery] = React.useState("");
    const [addBusy, setAddBusy] = React.useState(false);
    const [addMsg, setAddMsg] = React.useState({ type: "", text: "" });

    // Dialog: neue Domains
    const [openAdd, setOpenAdd] = React.useState(false);
    const [newDomains, setNewDomains] = React.useState("");
    const [newIsActive, setNewIsActive] = React.useState(true);
    const [newAiRating, setNewAiRating] = React.useState(false);
    const [newAiReason, setNewAiReason] = React.useState("");
    const [newAiProbability, setNewAiProbability] = React.useState("");

    // Dialog: Domain bearbeiten
    const [openEdit, setOpenEdit] = React.useState(false);
    const [editBusy, setEditBusy] = React.useState(false);
    const [editMsg, setEditMsg] = React.useState({ type: "", text: "" });
    const [editRow, setEditRow] = React.useState(null); // full row

    const [editDomain, setEditDomain] = React.useState("");
    const [editIsActive, setEditIsActive] = React.useState(true);
    const [editAiRating, setEditAiRating] = React.useState(false);
    const [editAiReason, setEditAiReason] = React.useState("");
    const [editAiProbability, setEditAiProbability] = React.useState("");

    // Dialog: Tags bearbeiten
    const [openTags, setOpenTags] = React.useState(false);
    const [tagDomain, setTagDomain] = React.useState(null); // {id, domain}
    const [tagInput, setTagInput] = React.useState("");
    const [tagBusy, setTagBusy] = React.useState(false);
    const [tagsError, setTagsError] = React.useState("");

    const {
        domains,
        loading,
        error,
        fetchBlocked,
        addDomains,
        updateDomain,
        deleteDomain,
        addTag,
        deleteTag,
    } = useBlockedDomains();

    /** Tags des aktuell ausgewählten Domains aus dem State ableiten */
    const currentTags = React.useMemo(() => {
        if (!tagDomain) return [];
        const row = domains.find((d) => d.id === tagDomain.id);
        return row?.tags || [];
    }, [domains, tagDomain]);

    /** --- Sortiert und filtert nach Query --- */
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        let data = Array.isArray(domains) ? [...domains] : [];

        if (q) {
            data = data.filter((row) => (row.domain || "").toLowerCase().includes(q));
        }

        data.sort((a, b) => {
            const idA = Number(a.id) || 0;
            const idB = Number(b.id) || 0;
            return idB - idA;
        });

        return data;
    }, [domains, query]);

    const handleOpenAdd = () => {
        setAddMsg({ type: "", text: "" });
        setNewIsActive(true);
        setNewAiRating(false);
        setNewAiReason("");
        setNewAiProbability("");
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        if (!addBusy) {
            setOpenAdd(false);
            setNewDomains("");
        }
    };

    const handleConfirmAdd = async () => {
        setAddMsg({ type: "", text: "" });
        setAddBusy(true);
        try {
            const prob = newAiProbability.trim() === "" ? null : Number(newAiProbability);
            const added = await addDomains(newDomains, newIsActive, {
                ai_rating: !!newAiRating,
                ai_reason: newAiReason.trim() ? newAiReason.trim() : null,
                ai_probability: prob,
            });
            setAddMsg({ type: "success", text: `Hinzugefügt: ${added.join(", ")}` });
            setNewDomains("");
            setOpenAdd(false);
        } catch (e) {
            setAddMsg({ type: "error", text: e?.message || "Konnte Domains nicht hinzufügen." });
        } finally {
            setAddBusy(false);
        }
    };

    const formatCreatedAt = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        if (isNaN(d)) return value;
        return fmtDE.format(d);
    };

    const formatProbability = (value) => {
        if (value == null || value === "") return "—";
        const n = Number(value);
        if (Number.isNaN(n)) return String(value);
        return n.toFixed(4);
    };

    const handleToggleActive = async (row) => {
        const next = !(row.is_active ?? true);
        try {
            await updateDomain(row.id, { is_active: next });
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeleteDomainClick = async (row) => {
        const ok = window.confirm(`Domain "${row.domain}" wirklich löschen?`);
        if (!ok) return;
        try {
            await deleteDomain(row.id);
        } catch (e) {
            console.error(e);
        }
    };

    const handleOpenTagsDialog = (row) => {
        setTagDomain({ id: row.id, domain: row.domain });
        setTagInput("");
        setTagsError("");
        setOpenTags(true);
    };

    const handleCloseTagsDialog = () => {
        if (tagBusy) return;
        setOpenTags(false);
        setTagDomain(null);
        setTagInput("");
        setTagsError("");
    };

    const handleAddTagClick = async () => {
        if (!tagDomain || !tagInput.trim()) return;
        setTagBusy(true);
        setTagsError("");
        try {
            await addTag(tagDomain.id, tagInput.trim());
            setTagInput("");
        } catch (e) {
            console.error(e);
            setTagsError(e?.message || "Konnte Tag nicht hinzufügen.");
        } finally {
            setTagBusy(false);
        }
    };

    const handleDeleteTagClick = async (tag) => {
        if (!tagDomain) return;
        setTagBusy(true);
        setTagsError("");
        try {
            await deleteTag(tagDomain.id, tag);
        } catch (e) {
            console.error(e);
            setTagsError(e?.message || "Konnte Tag nicht löschen.");
        } finally {
            setTagBusy(false);
        }
    };

    const handleOpenEditDialog = (row) => {
        setEditMsg({ type: "", text: "" });
        setEditRow(row);

        setEditDomain(row?.domain || "");
        setEditIsActive(row?.is_active ?? true);
        setEditAiRating(!!row?.ai_rating);
        setEditAiReason(row?.ai_reason || "");
        setEditAiProbability(
            row?.ai_probability == null || row?.ai_probability === ""
                ? ""
                : String(row.ai_probability)
        );

        setOpenEdit(true);
    };

    const handleCloseEditDialog = () => {
        if (editBusy) return;
        setOpenEdit(false);
        setEditRow(null);
        setEditDomain("");
        setEditIsActive(true);
        setEditAiRating(false);
        setEditAiReason("");
        setEditAiProbability("");
        setEditMsg({ type: "", text: "" });
    };

    const handleConfirmEdit = async () => {
        if (!editRow?.id) return;

        setEditMsg({ type: "", text: "" });
        setEditBusy(true);
        try {
            const prob = editAiProbability.trim() === "" ? null : Number(editAiProbability);
            await updateDomain(editRow.id, {
                domain: editDomain,
                is_active: !!editIsActive,
                ai_rating: !!editAiRating,
                ai_reason: editAiReason.trim() ? editAiReason.trim() : null,
                ai_probability: prob,
            });
            setEditMsg({ type: "success", text: "Gespeichert." });
            setOpenEdit(false);
        } catch (e) {
            setEditMsg({ type: "error", text: e?.message || "Konnte Domain nicht speichern." });
        } finally {
            setEditBusy(false);
        }
    };

    return (
        <PageContainer
            title="Excluded Domains"
            breadcrumbs={["Home", "Einstellungen", "Excluded Domains"]}
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={fetchBlocked}
                        disabled={loading || addBusy}
                    >
                        Aktualisieren
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleOpenAdd}
                        disabled={addBusy}
                    >
                        Domain hinzufügen
                    </Button>
                </Box>
            }
        >
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography>
                    Excluded Domains werden von der Suche nicht berücksichtigt und es wird nicht nach
                    weiteren Inhalten auf Seiten mit dieser Top Level Domain gesucht. Domains können
                    zusätzlich als aktiv/inaktiv markiert und mit Tags versehen werden.
                </Typography>
            </Paper>

            {/* Suche */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <TextField
                    placeholder="Suchen… (z. B. instagram)"
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </Paper>

            {/* Feedback aus Add-Aktion */}
            {addMsg.text && (
                <Alert
                    severity={addMsg.type === "success" ? "success" : "error"}
                    sx={{ mb: 2 }}
                    onClose={() => setAddMsg({ type: "", text: "" })}
                >
                    {addMsg.text}
                </Alert>
            )}

            {/* Fehler vom Laden */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchBlocked}>
                            Erneut laden
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Tabelle */}
            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={80}>ID</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell width={120}>Aktiv</TableCell>

                                <TableCell width={170}>KI Bewertung</TableCell>
                                <TableCell>KI Grund</TableCell>
                                <TableCell width={140}>Wahrscheinlichkeit</TableCell>

                                <TableCell>Tags</TableCell>
                                <TableCell>Erstellt</TableCell>
                                <TableCell width={280}>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((row, idx) => (
                                <TableRow key={row.id ?? `${row.domain}-${idx}`} hover>
                                    <TableCell>
                                        <Typography component="span" sx={{ fontFamily: "monospace" }}>
                                            {row.id ?? "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography component="span" sx={{ fontFamily: "monospace" }}>
                                            {row.domain}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={row.is_active ?? true}
                                            onChange={() => handleToggleActive(row)}
                                            inputProps={{ "aria-label": "Aktiv-Status umschalten" }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        {row.ai_rating ? (
                                            <Chip
                                                icon={<AutoAwesomeIcon />}
                                                label="gemini"
                                                variant="outlined"
                                                sx={{
                                                    fontFamily: "monospace",
                                                    fontWeight: 500,
                                                    background: "linear-gradient(90deg,#1a73e8,#9334e6)",
                                                    color: "white",
                                                    border: "none",
                                                    "& .MuiChip-icon": { color: "white" },
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                icon={<PersonIcon />}
                                                label="human"
                                                variant="outlined"
                                                sx={{
                                                    fontFamily: "monospace",
                                                    fontWeight: 500,
                                                }}
                                            />
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                            {row.ai_reason ? row.ai_reason : "—"}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>{formatProbability(row.ai_probability)}</TableCell>

                                    <TableCell>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {Array.isArray(row.tags) && row.tags.length > 0 ? (
                                                row.tags.map((t) => (
                                                    <Chip
                                                        key={t.id ?? `${row.id}-${t.tag}`}
                                                        label={t.tag}
                                                        size="small"
                                                    />
                                                ))
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    —
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>

                                    <TableCell>{formatCreatedAt(row.created_at)}</TableCell>

                                    <TableCell>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleOpenEditDialog(row)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<LabelIcon />}
                                                onClick={() => handleOpenTagsDialog(row)}
                                            >
                                                Tags
                                            </Button>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteDomainClick(row)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!filtered.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        Keine Einträge gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Dialog: Domains hinzufügen */}
            <Dialog open={openAdd} onClose={handleCloseAdd} fullWidth maxWidth="sm">
                <DialogTitle>Domain(s) hinzufügen</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Mehrere Domains durch Komma, Leerzeichen oder Zeilenumbruch trennen. Beispiele:{" "}
                            <code>instagram.com</code>, <code>sub.example.io</code>
                        </Typography>
                        <TextField
                            label="Domains"
                            placeholder="instagram.com, tiktok.com"
                            value={newDomains}
                            onChange={(e) => setNewDomains(e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            autoFocus
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={newIsActive}
                                onChange={(e) => setNewIsActive(e.target.checked)}
                            />
                            <Typography>Neue Domains als aktiv anlegen</Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={newAiRating}
                                onChange={(e) => setNewAiRating(e.target.checked)}
                            />
                            <Typography>KI Bewertung</Typography>
                        </Box>

                        <TextField
                            label="KI Grund"
                            value={newAiReason}
                            onChange={(e) => setNewAiReason(e.target.value)}
                            fullWidth
                        />

                        <TextField
                            label="Wahrscheinlichkeit"
                            value={newAiProbability}
                            onChange={(e) => setNewAiProbability(e.target.value)}
                            fullWidth
                            inputProps={{ inputMode: "decimal" }}
                            placeholder="z. B. 0.8734"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdd} disabled={addBusy}>
                        Abbrechen
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmAdd}
                        disabled={addBusy || !newDomains.trim()}
                    >
                        {addBusy ? "Speichere…" : "Hinzufügen"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Domain bearbeiten */}
            <Dialog open={openEdit} onClose={handleCloseEditDialog} fullWidth maxWidth="sm">
                <DialogTitle>Domain bearbeiten</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {editMsg.text && (
                            <Alert
                                severity={editMsg.type === "success" ? "success" : "error"}
                                onClose={() => setEditMsg({ type: "", text: "" })}
                            >
                                {editMsg.text}
                            </Alert>
                        )}

                        <TextField
                            label="Domain"
                            value={editDomain}
                            onChange={(e) => setEditDomain(e.target.value)}
                            fullWidth
                            autoFocus
                        />

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={editIsActive}
                                onChange={(e) => setEditIsActive(e.target.checked)}
                            />
                            <Typography>Aktiv</Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={editAiRating}
                                onChange={(e) => setEditAiRating(e.target.checked)}
                            />
                            <Typography>KI Bewertung</Typography>
                        </Box>

                        <TextField
                            label="KI Grund"
                            value={editAiReason}
                            onChange={(e) => setEditAiReason(e.target.value)}
                            fullWidth
                        />

                        <TextField
                            label="Wahrscheinlichkeit"
                            value={editAiProbability}
                            onChange={(e) => setEditAiProbability(e.target.value)}
                            fullWidth
                            inputProps={{ inputMode: "decimal" }}
                            placeholder="z. B. 0.8734"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} disabled={editBusy}>
                        Abbrechen
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmEdit}
                        disabled={editBusy || !editDomain.trim()}
                    >
                        {editBusy ? "Speichere…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Tags verwalten */}
            <Dialog open={openTags} onClose={handleCloseTagsDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    Tags für {tagDomain?.domain || "—"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {tagsError && (
                            <Alert
                                severity="error"
                                onClose={() => setTagsError("")}
                            >
                                {tagsError}
                            </Alert>
                        )}

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {currentTags.length ? (
                                currentTags.map((t) => (
                                    <Chip
                                        key={t.id ?? t.tag}
                                        label={t.tag}
                                        onDelete={() => handleDeleteTagClick(t.tag)}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Keine Tags vorhanden.
                                </Typography>
                            )}
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                label="Neuer Tag"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                fullWidth
                                disabled={tagBusy}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddTagClick}
                                disabled={tagBusy || !tagInput.trim()}
                            >
                                Hinzufügen
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTagsDialog} disabled={tagBusy}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
